import {useAppDispatch, useAppSelector} from "../../../app/store";
import {
    changeModal,
    closeModal, deleteTeam,
    EditTeamPayload,
    getTeam,
    NewTeamPayload,
    saveNewTeam,
    saveTeam
} from "../../../app/slices/modals/teamModal";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {ADMIN_LANG} from "../../../settings";
import toast from "react-hot-toast";
import {Button, Col, FormControl, InputGroup, Modal, Row, Form, Alert} from "react-bootstrap";
import AdminLoader from "../AdminLoader";
import CreatableSelect from "react-select/creatable";
import {Clipboard} from "react-bootstrap-icons";
import {useNavigate} from "react-router-dom";
import {isFulfilled} from "@reduxjs/toolkit";
import Confirm from "../Confirm";

const TeamModal = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const {
        isOpen,
        loading,
        error,
        team_id,
        name,
        domains,
        url,
        delete_message
    } = useAppSelector((state) => state.teamModal);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);

    const handleDelete = () => {
        setShowConfirmDelete(true)
    };

    const handleConfirm = () => {
        setShowConfirmDelete(false);
        if (team_id) {
            dispatch(deleteTeam(team_id)).then((resultAction) => {
                if (isFulfilled(resultAction)) {
                    navigate('/admin/teams');
                }
            });
        }
    };
    useEffect(() => {
        if (isOpen && team_id) {
            dispatch(getTeam(team_id));
        }
    }, [dispatch, isOpen, team_id]);

    const handleSave = useCallback((e: React.FormEvent) => {
        e.preventDefault();

        const payload = {
            name,
            domains,
            ...(team_id && {team_id})
        };

        const action = team_id ? saveTeam(payload as EditTeamPayload) : saveNewTeam(payload as NewTeamPayload);

        dispatch(action).then((resultAction) => {
            let newId = '0'
            if (resultAction.payload && resultAction.payload.team_id) {
                newId = resultAction.payload.team_id
            }
            if (!team_id && isFulfilled(resultAction)) {
                navigate(`/admin/teams/?new=${newId}`);
            }
        });
    }, [dispatch, team_id, name, domains, navigate]);

    const handleClose = useCallback(() => {
        dispatch(closeModal());
    }, [dispatch]);

    const handleCopyUrl = useCallback(() => {
        if (url) {
            navigator.clipboard.writeText(url)
                .then(() => toast.success(ADMIN_LANG.url_copied, {position: 'top-right'}))
                .catch(() => toast.error(ADMIN_LANG.url_copy_failed, {position: 'top-right'}));
        }
    }, [url]);

    const handleAllowedEmailsChange = useCallback((newValue: any) => {
        const emails = newValue.map((option: any) => option.value);
        dispatch(changeModal({domains: emails}));
    }, [dispatch]);

    const emailOptions = useMemo(() => (
        domains?.map(email => ({label: email, value: email})) || []
    ), [domains]);

    const modalTitle = team_id ? ADMIN_LANG.edit_team : ADMIN_LANG.new_team;

    return (
        <Modal size='xl' show={isOpen} onHide={handleClose} className='admin-modal'>
            {loading && <AdminLoader/>}
            <Modal.Header closeButton>
                <Modal.Title>{modalTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant='danger' dismissible>
                        <div dangerouslySetInnerHTML={{__html: error}}/>
                    </Alert>
                )}
                <Form id='team-modal-form' onSubmit={handleSave}>
                    <Row className='gy-4'>
                        <Col xs={12}>
                            <Form.Group controlId='name'>
                                <Form.Label>{ADMIN_LANG.name}</Form.Label>
                                <Form.Control
                                    required
                                    maxLength={255}
                                    type='text'
                                    value={name}
                                    onChange={(e) => dispatch(changeModal({name: e.target.value}))}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12}>
                            <Form.Group controlId='allowed_emails'>
                                <Form.Label>{ADMIN_LANG.allowed_emails}</Form.Label>
                                <CreatableSelect
                                    isMulti
                                    onChange={handleAllowedEmailsChange}
                                    options={emailOptions}
                                    value={emailOptions}
                                    noOptionsMessage={() => ADMIN_LANG.enter_emails_help}
                                    formatCreateLabel={(inputValue) => `${ADMIN_LANG.add} "${inputValue}"`}
                                    placeholder={ADMIN_LANG.enter_emails}
                                    classNamePrefix="react-select"
                                    className="custom-react-select"
                                />
                                <Form.Text className='help-text'>{ADMIN_LANG.enter_emails_help_text}</Form.Text>
                            </Form.Group>
                        </Col>
                        {url && (
                            <Col className='col-12'>
                                <Form.Label>{ADMIN_LANG.copy_team_url}</Form.Label>
                                <InputGroup>
                                    <FormControl type="text" value={url} readOnly/>
                                    <Button variant="success" onClick={handleCopyUrl}>
                                        <Clipboard/><span className='ms-2'>{ADMIN_LANG.copy_url}</span>
                                    </Button>
                                </InputGroup>
                            </Col>
                        )}
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                {team_id && (
                    <Button variant='danger' className='me-auto' onClick={handleDelete}>
                        {ADMIN_LANG.delete}
                    </Button>
                )}
                <Button variant='outline-primary' onClick={handleClose}>
                    {ADMIN_LANG.close}
                </Button>
                <Button variant='primary' type='submit' form='team-modal-form'>
                    {ADMIN_LANG.save}
                </Button>
            </Modal.Footer>
            <Confirm
                show={showConfirmDelete}
                message={delete_message}
                onConfirm={handleConfirm}
                onCancel={() => setShowConfirmDelete(false)}
            />

        </Modal>
    );
}

export default TeamModal;